import React, { useEffect, useRef, useCallback } from 'react';
import styles from './Keyboard.module.css';

const WHITE_NOTES = ['C4','D4','E4','F4','G4','A4','B4','C5','D5','E5','F5','G5','A5','B5'];
const BLACK_NOTES = [
  { note: 'C#4', after: 0 },
  { note: 'D#4', after: 1 },
  { note: 'F#4', after: 3 },
  { note: 'G#4', after: 4 },
  { note: 'A#4', after: 5 },
  { note: 'C#5', after: 7 },
  { note: 'D#5', after: 8 },
  { note: 'F#5', after: 10 },
  { note: 'G#5', after: 11 },
  { note: 'A#5', after: 12 },
];

const KB_WHITE = ['a','s','d','f','g','h','j','k','l',';',"'",'z','x','c'];
const KB_BLACK_MAP = {
  'C#4': 'w', 'D#4': 'e', 'F#4': 't', 'G#4': 'y', 'A#4': 'u',
  'C#5': 'o', 'D#5': 'p', 'F#5': '[', 'G#5': ']', 'A#5': '\\'
};

const NOTE_SYMBOLS = ['♩','♪','♫','♬'];

export default function Keyboard({ pressedKeys, onKeyPress, onKeyRelease, isDisabled = false }) {
  const wrapRef = useRef(null);
  const particleContainerRef = useRef(null);
  const keyboardHeldMouse = useRef(new Set());

  const spawnParticle = useCallback((note) => {
    const container = particleContainerRef.current;
    const keyEl = wrapRef.current?.querySelector(`[data-note="${note}"]`);
    if (!container || !keyEl) return;

    const keyRect = keyEl.getBoundingClientRect();
    const wrapRect = wrapRef.current.getBoundingClientRect();

    const p = document.createElement('div');
    p.className = styles.particle;
    p.textContent = NOTE_SYMBOLS[Math.floor(Math.random() * NOTE_SYMBOLS.length)];
    p.style.left = (keyRect.left - wrapRect.left + keyRect.width / 2 - 8) + 'px';
    p.style.top = (keyRect.top - wrapRect.top - 8) + 'px';
    container.appendChild(p);
    setTimeout(() => p.remove(), 900);
  }, []);

  const handlePress = useCallback((note, velocity = 0.8) => {
    if (isDisabled || pressedKeys.has(note)) return;
    onKeyPress(note, velocity);
    spawnParticle(note);
  }, [pressedKeys, onKeyPress, spawnParticle]);

  const handleRelease = useCallback((note) => {
    if (isDisabled) return;
    onKeyRelease(note);
  }, [isDisabled, onKeyRelease]);

  // Calculate velocity from mouse/touch event
  const getVelocity = useCallback((e) => {
    let y = 0;
    if (e.clientY !== undefined) y = e.clientY;
    else if (e.touches?.[0]) y = e.touches[0].clientY;
    
    if (!wrapRef.current) return 0.8;
    const rect = wrapRef.current.getBoundingClientRect();
    // y=0 (top) = hard press (velocity ~1), y=rect.height (bottom) = soft (velocity ~0.5)
    const relY = Math.max(0, Math.min(1, (y - rect.top) / rect.height));
    return Math.max(0.5, 1 - relY * 0.5); // range 1.0 to 0.5
  }, []);

  // Keyboard listeners
  useEffect(() => {
    const keyToNoteMap = {};
    KB_WHITE.forEach((k, i) => {
      if (WHITE_NOTES[i]) keyToNoteMap[k] = WHITE_NOTES[i];
    });
    Object.entries(KB_BLACK_MAP).forEach(([note, key]) => {
      keyToNoteMap[key] = note;
    });

    const getMappedNote = (key) => keyToNoteMap[key.toLowerCase()];

    const onKeyDown = (e) => {
      if (isDisabled || e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      if (e.repeat) return;
      const mapped = getMappedNote(e.key);
      if (mapped) handlePress(mapped);
    };

    const onKeyUp = (e) => {
      if (isDisabled || e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      const mapped = getMappedNote(e.key);
      if (mapped) handleRelease(mapped);
    };

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
    };
  }, [isDisabled, handlePress, handleRelease]);

  // Global mouseup to release any held keys
  useEffect(() => {
    const onMouseUp = () => {
      keyboardHeldMouse.current.forEach(note => handleRelease(note));
      keyboardHeldMouse.current.clear();
    };
    window.addEventListener('mouseup', onMouseUp);
    return () => window.removeEventListener('mouseup', onMouseUp);
  }, [handleRelease]);

  return (
    <div className={styles.wrap} ref={wrapRef}>
      <div ref={particleContainerRef} className={styles.particles} />

      <div className={styles.keyboard}>
        {/* White keys */}
        <div className={styles.whiteKeys}>
          {WHITE_NOTES.map((note, i) => (
            <div
              key={note}
              data-note={note}
              className={`${styles.whiteKey} ${pressedKeys.has(note) ? styles.pressed : ''}`}
              onMouseDown={(e) => { e.preventDefault(); keyboardHeldMouse.current.add(note); handlePress(note, getVelocity(e)); }}
              onMouseUp={() => { keyboardHeldMouse.current.delete(note); handleRelease(note); }}
              onMouseEnter={(e) => { if (e.buttons === 1) { keyboardHeldMouse.current.add(note); handlePress(note, getVelocity(e)); } }}
              onMouseLeave={() => { if (keyboardHeldMouse.current.has(note)) { keyboardHeldMouse.current.delete(note); handleRelease(note); } }}
              onTouchStart={(e) => { e.preventDefault(); handlePress(note, getVelocity(e)); }}
              onTouchEnd={() => handleRelease(note)}
            >
              <span className={styles.keyLabel}>{KB_WHITE[i]}</span>
            </div>
          ))}
        </div>

        {/* Black keys overlay */}
        <div className={styles.blackKeys}>
          {BLACK_NOTES.map(({ note, after }) => {
            const whiteKeyWidthPct = 100 / WHITE_NOTES.length;
            const leftPct = (after + 1) * whiteKeyWidthPct - (whiteKeyWidthPct * 0.3);
            return (
              <div
                key={note}
                data-note={note}
                className={`${styles.blackKey} ${pressedKeys.has(note) ? styles.pressedBlack : ''}`}
                style={{ left: `${leftPct}%` }}
                onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); keyboardHeldMouse.current.add(note); handlePress(note, getVelocity(e)); }}
                onMouseUp={(e) => { e.stopPropagation(); keyboardHeldMouse.current.delete(note); handleRelease(note); }}
                onMouseLeave={() => { if (keyboardHeldMouse.current.has(note)) { keyboardHeldMouse.current.delete(note); handleRelease(note); } }}
                onTouchStart={(e) => { e.preventDefault(); e.stopPropagation(); handlePress(note, getVelocity(e)); }}
                onTouchEnd={(e) => { e.stopPropagation(); handleRelease(note); }}
              >
                <span className={styles.blackKeyLabel}>{KB_BLACK_MAP[note]}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
